import { createContext, useContext, useState, useEffect } from 'react';
import { getUserByPhone, autoRegisterUser, getBusiness } from '../lib/api';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const STORAGE_KEY = 'noteho_auth';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    restoreSession();
  }, []);

  const restoreSession = async () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.phone) {
          const fullPhone = parsed.phone.startsWith('+') ? parsed.phone : `+91${parsed.phone.replace(/^91/, '')}`;
          const response = await getUserByPhone(fullPhone);
          if (response.data) {
            setUser({ phone: fullPhone, id: response.data.id });
            setUserProfile(response.data);
            setBusiness(response.data.businesses);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }
    } catch (error) {
      console.error('Session restore failed:', error);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  const persistSession = (phone) => {
    const fullPhone = phone.startsWith('+') ? phone : `+91${phone.replace(/^91/, '')}`;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ phone: fullPhone }));
  };

  const signInWithOTP = async (phone) => {
    return { data: { phone }, error: null };
  };

  const verifyOTP = async (phone, token) => {
    if (token !== '123456') {
      return { data: null, error: { message: 'Invalid OTP. Use 123456.' } };
    }
    try {
      const response = await getUserByPhone(phone);
      if (response.data) {
        setUser({ phone, id: response.data.id });
        setUserProfile(response.data);
        setBusiness(response.data.businesses);
        persistSession(phone);
        setLoading(false);
        return { data: response.data, error: null };
      }
      const registerResponse = await autoRegisterUser(phone);
      if (registerResponse.data) {
        setUser({ phone, id: registerResponse.data.id });
        setUserProfile(registerResponse.data);
        setBusiness(registerResponse.data.businesses);
        persistSession(phone);
        setLoading(false);
        return { data: registerResponse.data, error: null };
      }
      return { data: null, error: { message: 'Registration failed. Please try again.' } };
    } catch (error) {
      return { data: null, error: { message: error.message } };
    }
  };

  const signOut = async () => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setUserProfile(null);
    setBusiness(null);
    return { error: null };
  };

  const demoLogin = async (phone) => {
    try {
      const response = await getUserByPhone(phone);
      if (response.data) {
        setUser({ phone, id: response.data.id });
        setUserProfile(response.data);
        setBusiness(response.data.businesses);
        persistSession(phone);
        setLoading(false);
        return { data: response.data, error: null };
      }
      return { data: null, error: 'User not found' };
    } catch (error) {
      return { data: null, error: error.message };
    }
  };

  const refreshBusiness = async () => {
    if (!business?.id) return;
    try {
      const r = await getBusiness(business.id);
      if (r.data) setBusiness(r.data);
    } catch (e) { console.error('refreshBusiness failed', e); }
  };

  const value = {
    user,
    userProfile,
    business,
    loading,
    signInWithOTP,
    verifyOTP,
    signOut,
    demoLogin,
    refreshBusiness
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
